import { useEffect, useRef, useCallback } from 'react';

import { useRouter } from 'next/router';

import { GenerationStatus } from '../../enums/generationStatus';
import { PageKey } from '../../enums/pageKey';
import { Portal } from '../../enums/portal.enum';
import { RecentGenerationsStorage } from '../../services/recentGenerationStorage';
import { FormContents } from '../../types/page';

interface UseGenerationSaveProps {
  isStreamDone: boolean;
  isDoneRequesting: boolean;
  responseToShow: string;
  pageKey: PageKey;
  selectedPortal: Portal;
  isLoading?: boolean;
  subject?: string;
  existingGenerationId?: string;
  shouldUseCache?: boolean;
  isDllContinuation?: boolean;
  inputs?: FormContents;
}

export function useGenerationSave({
  isStreamDone,
  isDoneRequesting,
  responseToShow,
  pageKey,
  selectedPortal,
  isLoading = false,
  subject,
  existingGenerationId,
  shouldUseCache = false,
  isDllContinuation = false,
  inputs,
}: UseGenerationSaveProps): void {
  const router = useRouter();
  const generationIdFromUrl = router?.query?.id as string;

  const lastSavedContentRef = useRef<string>('');
  const isActiveGenerationRef = useRef<boolean>(false);
  const wasLoadingRef = useRef<boolean>(false);
  const newGenerationIdRef = useRef<string | null>(null);
  const hasCompletedRef = useRef<boolean>(false);

  const generationIdRef = useRef<string | null>(null);

  const pageKeyRef = useRef(pageKey);
  const selectedPortalRef = useRef(selectedPortal);
  const existingGenerationIdRef = useRef(existingGenerationId);
  const shouldUseCacheRef = useRef(shouldUseCache);
  const inputsRef = useRef(inputs);

  const lastSaveTimeRef = useRef<number>(0);
  const SAVE_DEBOUNCE_MS = 500;

  pageKeyRef.current = pageKey;
  selectedPortalRef.current = selectedPortal;
  existingGenerationIdRef.current = existingGenerationId;
  shouldUseCacheRef.current = shouldUseCache;
  inputsRef.current = inputs;

  const saveGeneration = useCallback(
    (content: string, status: GenerationStatus, subject: string) => {
      const storage = RecentGenerationsStorage.getInstance();

      let generationIdToUse: string | undefined;

      if (shouldUseCacheRef.current) {
        generationIdToUse = existingGenerationIdRef.current;
      } else if (isDllContinuation && generationIdFromUrl) {
        generationIdToUse = generationIdFromUrl;
      } else if (newGenerationIdRef.current) {
        generationIdToUse = newGenerationIdRef.current;
      }

      if (shouldUseCacheRef.current && !existingGenerationIdRef.current) {
        return;
      }

      if (generationIdToUse) {
        storage.storeGeneration(
          pageKeyRef.current,
          selectedPortalRef.current,
          content,
          status,
          generationIdToUse,
          subject,
          inputsRef.current,
        );
      } else {
        const result = storage.storeGeneration(
          pageKeyRef.current,
          selectedPortalRef.current,
          content,
          status,
          undefined,
          subject,
          inputsRef.current,
        );

        if (result.success) {
          newGenerationIdRef.current = result.id;
          generationIdRef.current = result.id;
        }
      }

      lastSavedContentRef.current = content;
    },
    [isDllContinuation, generationIdFromUrl],
  );

  useEffect(() => {
    if (isLoading && !wasLoadingRef.current) {
      isActiveGenerationRef.current = true;
      hasCompletedRef.current = false;

      if (!shouldUseCache && !isDllContinuation) {
        newGenerationIdRef.current = null;
      }

      lastSavedContentRef.current = '';
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading, existingGenerationId, shouldUseCache, isDllContinuation]);

  useEffect(() => {
    if (isStreamDone && isDoneRequesting && !isLoading) {
      isActiveGenerationRef.current = false;
    }
  }, [isStreamDone, isDoneRequesting, isLoading]);

  useEffect(() => {
    if (
      isActiveGenerationRef.current &&
      responseToShow &&
      responseToShow.trim() &&
      responseToShow !== lastSavedContentRef.current &&
      !hasCompletedRef.current
    ) {
      const now = Date.now();
      const status = GenerationStatus.INCOMPLETE;

      const shouldSave = !(isStreamDone && isDoneRequesting);

      if (shouldSave && now - lastSaveTimeRef.current >= SAVE_DEBOUNCE_MS) {
        saveGeneration(responseToShow, status, subject);
        lastSaveTimeRef.current = now;
      }
    }
  }, [responseToShow, isStreamDone, isDoneRequesting, subject, saveGeneration]);

  useEffect(() => {
    const shouldWaitForCacheSetup =
      generationIdFromUrl && (!shouldUseCache || !existingGenerationId);

    if (
      isStreamDone &&
      isDoneRequesting &&
      responseToShow &&
      !isLoading &&
      !shouldWaitForCacheSetup &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      saveGeneration(responseToShow, GenerationStatus.COMPLETE, subject);
    }
  }, [
    isStreamDone,
    isDoneRequesting,
    responseToShow,
    isLoading,
    subject,
    shouldUseCache,
    generationIdFromUrl,
    existingGenerationId,
    saveGeneration,
  ]);
}
